/**
 * Which field the teacher last acted on.
 *
 * A step is built by diffing the block's data before and after, and the diff cannot tell the act
 * from its consequences. Clicking "accept integer answers only" records six changed fields — the
 * switch itself, the error mode it forces, and four more the block rewrites in passing — and the
 * ring landed on whichever the diff happened to list first. That is how a grading-mode control lit
 * up for an edit nobody made near it.
 *
 * The control knows what it is; nothing was asking. This listens at the one place every control
 * already declares itself — `data-field-id` — and remembers the last one touched, so the step can
 * be built knowing which of its fields is the act and which are the fallout.
 *
 * Deliberately a listener rather than a prop threaded through fifteen blocks: `onUpdate(data)`
 * carries no provenance, and giving it some would mean changing every control in the editor to
 * report something it has already declared in its markup.
 */

let lastTouched: string | null = null;

function remember(e: Event) {
  const el = (e.target as Element | null)?.closest?.('[data-field-id]');
  const id = el?.getAttribute('data-field-id');
  if (id) lastTouched = id;
}

/** Start watching. Returns the cleanup, for an effect. */
export function watchFieldProvenance(): () => void {
  /**
   * Three events, all in the CAPTURE phase so they run before the handler that makes the edit.
   *
   * `pointerdown` and `focusin` cover a real click, a touch and a tab. `click` is there for the
   * programmatic kind — `element.click()` dispatches no pointer events, so without it a test drives
   * a control the provenance never sees, and would pass while the product stayed broken.
   */
  const events = ['pointerdown', 'focusin', 'click'] as const;
  for (const type of events) document.addEventListener(type, remember, true);
  return () => { for (const type of events) document.removeEventListener(type, remember, true); };
}

/** The field the teacher last touched, if it is one of `candidates`. */
export function actedOn(candidates: readonly string[]): string | null {
  return lastTouched && candidates.includes(lastTouched) ? lastTouched : null;
}

/** Test seam: forget what was touched. */
export function resetFieldProvenance(): void {
  lastTouched = null;
}
